import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Cookie, X } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'cookie_consent';
const COOKIE_MAX_AGE_DAYS = 365;

type ConsentValue = 'accepted' | 'declined';

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value};expires=${expires};path=/;SameSite=Lax`;
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? match[1] : null;
}

function getConsent(): ConsentValue | null {
  // Check cookie first (survives ITP), fall back to localStorage
  return (getCookie(COOKIE_CONSENT_KEY) as ConsentValue) ||
    (localStorage.getItem(COOKIE_CONSENT_KEY) as ConsentValue) || null;
}

function saveConsent(value: ConsentValue) {
  setCookie(COOKIE_CONSENT_KEY, value, COOKIE_MAX_AGE_DAYS);
  localStorage.setItem(COOKIE_CONSENT_KEY, value);
}

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

function updateGtagConsent(granted: boolean) {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: granted ? 'granted' : 'denied',
    });
  }
}

export function CookieConsent() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (!consent) {
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'default', {
          analytics_storage: 'denied',
        });
      }
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    } else {
      updateGtagConsent(consent === 'accepted');
    }
  }, []);

  const handleAccept = () => {
    saveConsent('accepted');
    updateGtagConsent(true);
    setVisible(false);
  };

  const handleDecline = () => {
    saveConsent('declined');
    updateGtagConsent(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 animate-in slide-in-from-bottom-4 duration-500">
      <div className="mx-auto max-w-3xl rounded-xl border border-border bg-card shadow-lg p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <Cookie className="w-6 h-6 text-primary shrink-0 mt-0.5 sm:mt-0" />
        <p className="text-sm text-muted-foreground flex-1">
          {t('cookies.message', 'We use cookies and analytics to improve your experience. By accepting, you allow us to collect anonymous usage data via Google Analytics.')}
          {' '}
          <a href="/en/cookie-policy" className="underline text-primary hover:text-primary/80 transition-colors">
            {t('cookies.learnMore', 'Learn more')}
          </a>
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDecline}
            className="text-xs"
          >
            {t('cookies.decline', 'Decline')}
          </Button>
          <Button
            size="sm"
            onClick={handleAccept}
            className="text-xs"
          >
            {t('cookies.accept', 'Accept')}
          </Button>
        </div>
      </div>
    </div>
  );
}
